import { createBusinessRuntime } from './business-runtime.ts';
import type {
  SmtRuntimeBootstrapBlocked,
  SmtRuntimeBootstrapBlockedCode,
  SmtRuntimeBootstrapResult,
  SmtRuntimeCompositionInput,
} from './smt-runtime-bootstrap-contract.ts';

const blocked = (
  input: SmtRuntimeCompositionInput,
  code: SmtRuntimeBootstrapBlockedCode,
  causeCode?: string,
): SmtRuntimeBootstrapBlocked => ({
  kind: 'blocked',
  profile: input.profile,
  code,
  ...(causeCode ? { causeCode } : {}),
});

const hasText = (value: string): boolean => value.trim().length > 0;

const isBusinessDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

export function composeSmtBusinessRuntime(input: SmtRuntimeCompositionInput): SmtRuntimeBootstrapResult {
  if (!hasText(input.storeId)) return blocked(input, 'SMT_BOOTSTRAP_STORE_ID_REQUIRED');
  if (!isBusinessDate(input.businessDate)) return blocked(input, 'SMT_BOOTSTRAP_BUSINESS_DATE_INVALID');
  if (!hasText(input.operatorId)) return blocked(input, 'SMT_BOOTSTRAP_OPERATOR_REQUIRED');
  if (!hasText(input.deviceId)) return blocked(input, 'SMT_BOOTSTRAP_DEVICE_REQUIRED');

  if (input.profile === 'production' && input.evidence.durability !== 'durable') {
    return blocked(input, 'SMT_BOOTSTRAP_PRODUCTION_DURABILITY_REQUIRED');
  }
  if (input.profile === 'production' && input.evidence.printExecution !== 'native-capable') {
    return blocked(input, 'SMT_BOOTSTRAP_PRODUCTION_NATIVE_PRINT_REQUIRED');
  }

  try {
    if (input.dependencies.getStoreId() !== input.storeId) {
      return blocked(input, 'SMT_BOOTSTRAP_STORE_MISMATCH');
    }
    if (input.dependencies.getBusinessDate() !== input.businessDate) {
      return blocked(input, 'SMT_BOOTSTRAP_BUSINESS_DATE_MISMATCH');
    }

    const policy = input.dependencies.getActivePolicy();
    if (!policy) return blocked(input, 'SMT_BOOTSTRAP_POLICY_UNAVAILABLE');
    if (!input.dependencies.isStoreReady()) return blocked(input, 'SMT_BOOTSTRAP_STORE_NOT_READY');

    const operator = input.dependencies.staff.staff(input.operatorId);
    if (!operator || operator.status !== 'active') {
      return blocked(input, 'SMT_BOOTSTRAP_OPERATOR_NOT_ACTIVE');
    }

    for (const category of input.adapterInputs.categories) {
      for (const productId of category.productIds) {
        if (!input.dependencies.getProduct(productId)) {
          return blocked(input, 'SMT_BOOTSTRAP_CATALOG_PRODUCT_NOT_FOUND');
        }
      }
    }

    const availability = input.dependencies.availability.projection();
    if (availability.storeId !== input.storeId) {
      return blocked(input, 'SMT_BOOTSTRAP_AVAILABILITY_STORE_MISMATCH');
    }

    const table = input.dependencies.table.projection();
    if (table.storeId !== input.storeId) return blocked(input, 'SMT_BOOTSTRAP_TABLE_STORE_MISMATCH');
    if (table.businessDate !== input.businessDate) {
      return blocked(input, 'SMT_BOOTSTRAP_TABLE_BUSINESS_DATE_MISMATCH');
    }

    return {
      kind: 'ready',
      profile: input.profile,
      runtime: createBusinessRuntime(input.dependencies),
      adapterInputs: input.adapterInputs,
      evidence: input.evidence,
    };
  } catch (error: unknown) {
    return blocked(
      input,
      'SMT_BOOTSTRAP_COMPOSITION_FAILED',
      error instanceof Error ? error.message : 'SMT_BOOTSTRAP_UNKNOWN_ERROR',
    );
  }
}
